import { z } from 'zod';

// Allowed app (vendor-selected subset for a device)
export const AllowedAppSchema = z.object({
  executableName: z.string().min(1).max(255),
  displayName: z.string().min(1).max(255),
});
export const AllowedAppsSchema = z.array(AllowedAppSchema).max(100);

// Discovered app (auto-scanned by agent from Windows registry)
export const DiscoveredAppSchema = z.object({
  executableName: z.string().min(1).max(255),
  displayName: z.string().min(1).max(255),
  publisher: z.string().max(255).optional(),
  installPath: z.string().max(500).optional(),
});
export const DiscoveredAppsSchema = z.array(DiscoveredAppSchema).max(500);

// Device health info (reported in heartbeat)
export const DeviceHealthSchema = z.object({
  cpuPercent: z.number().min(0).max(100).optional(),
  ramPercent: z.number().min(0).max(100).optional(),
  diskFreeGb: z.number().min(0).optional(),
});

// Heartbeat payload
export const HeartbeatPayloadSchema = z.object({
  agentVersion: z.string().max(50).optional(),
  osInfo: z
    .object({
      os: z.string().max(100).optional(),
      arch: z.string().max(50).optional(),
      hostname: z.string().max(255).optional(),
    })
    .merge(DeviceHealthSchema)
    .optional(),
  discoveredApps: DiscoveredAppsSchema.optional(),
});

// Pairing request
export const PairRequestSchema = z.object({
  venueId: z.string().uuid(),
  pairingCode: z.string().regex(/^\d{6}$/, 'Pairing code must be exactly 6 digits'),
});

// Command result
export const CommandResultSchema = z.object({
  status: z.enum(['EXECUTED', 'FAILED']),
  error: z.string().max(1000).optional(),
});

// Vendor: Set allowed apps
export const SetAllowedAppsSchema = z.object({
  allowedApps: AllowedAppsSchema,
});

// Vendor: Bulk set allowed apps
export const BulkSetAllowedAppsSchema = z.object({
  deviceIds: z.array(z.string().uuid()).min(1).max(500),
  allowedApps: AllowedAppsSchema,
});

// Per-command-type payload schemas (used in superRefine validation)
export const KillProcessPayloadSchema = z.object({
  processName: z
    .string()
    .min(1)
    .max(260)
    .regex(/^[\w\-. ]+$/, 'Process name contains invalid characters'),
});

export const ShowMessagePayloadSchema = z.object({
  title: z.string().max(100).optional(),
  message: z.string().min(1).max(1000),
  durationMs: z.number().int().min(1000).max(300000).optional(),
});

const AGENT_COMMAND_TYPES = [
  'LOCK',
  'UNLOCK',
  'KILL_PROCESS',
  'UPDATE_WHITELIST',
  'SHOW_MESSAGE',
  'SCAN_APPS',
] as const;

export type AgentCommandTypeEnum = (typeof AGENT_COMMAND_TYPES)[number];

// Vendor: Send command
export const SendCommandSchema = z
  .object({
    command: z.enum(AGENT_COMMAND_TYPES),
    payload: z.record(z.unknown()).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.command === 'KILL_PROCESS') {
      const result = KillProcessPayloadSchema.safeParse(data.payload);
      if (!result.success) {
        for (const issue of result.error.issues) {
          ctx.addIssue({ ...issue, path: ['payload', ...issue.path] });
        }
      }
    }
    if (data.command === 'SHOW_MESSAGE') {
      const result = ShowMessagePayloadSchema.safeParse(data.payload);
      if (!result.success) {
        for (const issue of result.error.issues) {
          ctx.addIssue({ ...issue, path: ['payload', ...issue.path] });
        }
      }
    }
  });

// Vendor: Generate pairing code
export const GeneratePairCodeSchema = z.object({
  inventoryUnitId: z.string().uuid(),
});

// Vendor: Webhook config

/**
 * Validate that a URL is safe for server-side requests (no SSRF)
 */
function isSafeWebhookUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return false;
    const hostname = parsed.hostname.toLowerCase();
    // Reject localhost variants
    if (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '::1' ||
      hostname === '0.0.0.0'
    )
      return false;
    // Reject private IP ranges
    const parts = hostname.split('.');
    if (parts.length === 4 && parts.every((p) => /^\d+$/.test(p))) {
      const [a, b] = parts.map(Number);
      if (a === 10) return false; // 10.0.0.0/8
      if (a === 172 && b >= 16 && b <= 31) return false; // 172.16.0.0/12
      if (a === 192 && b === 168) return false; // 192.168.0.0/16
      if (a === 169 && b === 254) return false; // 169.254.0.0/16 (link-local)
    }
    // Reject .local and .internal TLDs
    if (hostname.endsWith('.local') || hostname.endsWith('.internal')) return false;
    return true;
  } catch {
    return false;
  }
}

export const WebhookConfigSchema = z.object({
  webhookUrl: z
    .string()
    .url()
    .max(2048)
    .refine(isSafeWebhookUrl, {
      message: 'Webhook URL must use HTTPS and not point to private/internal addresses',
    })
    .nullable(),
});

// Types
export type AllowedApp = z.infer<typeof AllowedAppSchema>;
export type DiscoveredApp = z.infer<typeof DiscoveredAppSchema>;
export type DeviceHealth = z.infer<typeof DeviceHealthSchema>;
export type HeartbeatPayload = z.infer<typeof HeartbeatPayloadSchema>;
export type PairRequest = z.infer<typeof PairRequestSchema>;
export type CommandResult = z.infer<typeof CommandResultSchema>;
export type SetAllowedApps = z.infer<typeof SetAllowedAppsSchema>;
export type BulkSetAllowedApps = z.infer<typeof BulkSetAllowedAppsSchema>;
export type SendCommand = z.infer<typeof SendCommandSchema>;
export type KillProcessPayload = z.infer<typeof KillProcessPayloadSchema>;
export type ShowMessagePayload = z.infer<typeof ShowMessagePayloadSchema>;
export type GeneratePairCode = z.infer<typeof GeneratePairCodeSchema>;
export type WebhookConfig = z.infer<typeof WebhookConfigSchema>;
